import type { Flow } from "@learnengine/shared-types";

export interface OrchestratorClientOptions {
  baseUrl: string;
  userId: string;
  fetchFn?: typeof fetch;
}

export interface ProgressPayload {
  flowId: string;
  completedSeconds: number;
  activeSeconds: number;
}

export interface ProgressSnapshotResponse {
  flowId: string;
  completedSeconds: number;
  activeSeconds: number;
  updatedAt: string;
}

export class OrchestratorClient {
  private readonly baseUrl: string;
  private readonly userId: string;
  private readonly fetchFn: typeof fetch;

  constructor(options: OrchestratorClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.userId = options.userId;
    this.fetchFn = options.fetchFn ?? fetch;
  }

  async loadFlow(flowId: string): Promise<Flow> {
    const res = await this.fetchFn(`${this.baseUrl}/flows/${encodeURIComponent(flowId)}`, {
      method: "GET",
      headers: {
        "x-user-id": this.userId,
      },
    });

    if (!res.ok) {
      throw new Error(`loadFlow failed: ${res.status}`);
    }

    const body: any = await res.json();
    if (!body?.ok || !body?.flow) {
      throw new Error("loadFlow failed: invalid response");
    }
    return body.flow as Flow;
  }

  async saveProgress(payload: ProgressPayload): Promise<void> {
    const res = await this.fetchFn(`${this.baseUrl}/progress`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-user-id": this.userId,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error(`saveProgress failed: ${res.status}`);
    }

    const body: any = await res.json().catch(() => null);
    if (body && body.ok === false) {
      throw new Error("saveProgress failed: server returned ok=false");
    }
  }

  async loadProgress(flowId: string): Promise<ProgressSnapshotResponse | null> {
    const res = await this.fetchFn(`${this.baseUrl}/progress/${encodeURIComponent(flowId)}`, {
      method: "GET",
      headers: {
        "x-user-id": this.userId,
      },
    });

    if (!res.ok) {
      throw new Error(`loadProgress failed: ${res.status}`);
    }

    const body: any = await res.json();
    if (!body?.ok) {
      throw new Error("loadProgress failed: invalid response");
    }
    return (body.progress ?? null) as ProgressSnapshotResponse | null;
  }
}
